/**
 * Scope 2 Emissions Detail Page
 *
 * Indirect emissions from purchased energy with dual reporting support.
 * Shows location-based and market-based emissions per GHG Protocol.
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { PieChart, Pie, Cell } from 'recharts';
import { ArrowRight, Zap, Loader2, Inbox, MapPin, ShoppingCart } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@g17eco/ui-modern/components/ui/chart';
import { SCOPE_TAILWIND } from '../config/scopeBranding';
import { ACTIVITY_TYPE_COLORS, ACTIVITY_TYPE_FA_ICONS, formatActivityType } from '../config/activityTypeBranding';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { TabNav } from '@g17eco/ui-modern/components/ui/tab-nav';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  useGetEmissionsSummaryQuery,
  useGetActivitiesQuery,
  useGetScope2DualSummaryQuery,
  useGetEmissionsByActivityTypeQuery,
  useTriggerCalculationMutation,
} from '../api/emissions';
import { GasBreakdownPanel } from '../components/GasBreakdownPanel';
import { AssetBreakdownPanel } from '../components/AssetBreakdownPanel';
import { Scope2ComparisonCard } from '../components/Scope2ComparisonCard';
import { Scope2SupplementaryPanel } from '../components/Scope2SupplementaryPanel';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl, generateUrlWithQuery } from '@routes/util';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { EmissionScope, ActivityUnit, Scope2Method } from '../types/emissions';
import { EmissionsScopeNav } from '../components/EmissionsNav';
import { FiscalYearSelector, getFiscalYearPeriod } from '../components/FiscalYearSelector';
import { useAppSelector } from '@reducers/index';
import { TopEmissionSourcesCard } from '../components/TopEmissionSourcesCard';
import { RecentActivitiesCard } from '../components/RecentActivitiesCard';
import { CalculationDetailsDrawer } from '../components/CalculationDetailsDrawer';
import { IncludeChildrenToggle } from '../components/IncludeChildrenToggle';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';
import { useCalculationDrawer } from '../hooks/useCalculationDrawer';
import { getInitiativeTypeTree } from '../../../selectors/initiativeTree';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';

export const EmissionsScope2Page = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const history = useHistory();
  const {
    hasChildren,
    selectedFiscalYear,
    setSelectedFiscalYear,
    availableYears,
    includeChildren,
    setIncludeChildren,
  } = useEmissionsFilters();
  const initiativeTypeTree = useAppSelector(getInitiativeTypeTree);
  const initiativeLabelMap = useMemo(
    () => new Map(initiativeTypeTree.map((i) => [i._id, i.name])),
    [initiativeTypeTree],
  );
  const getReportingLevelLabel = useCallback(
    (activityInitiativeId: string) =>
      activityInitiativeId === initiativeId ? (
        <Badge variant='default'>Current Level</Badge>
      ) : (
        <Badge variant='outline'>{initiativeLabelMap.get(activityInitiativeId) ?? activityInitiativeId}</Badge>
      ),
    [initiativeId, initiativeLabelMap],
  );
  const [scope2Method, setScope2Method] = useState<Scope2Method>(Scope2Method.LocationBased);
  const [recalculatingActivityId, setRecalculatingActivityId] = useState<string | null>(null);
  const { selectedActivity, openDrawer, closeDrawer } = useCalculationDrawer();
  const [triggerCalculation] = useTriggerCalculationMutation();

  const handleRecalculate = useCallback(
    async (activityId: string, activityInitiativeId: string) => {
      setRecalculatingActivityId(activityId);
      try {
        await triggerCalculation({
          initiativeId: activityInitiativeId,
          activityId,
          options: { recalculate: true },
        }).unwrap();
        generateToast({ color: 'success', message: 'Emissions recalculated successfully' });
      } catch (error) {
        toastFromError({ error, title: 'Recalculation failed' });
      } finally {
        setRecalculatingActivityId(null);
      }
    },
    [triggerCalculation],
  );
  const periodDates = useMemo(() => getFiscalYearPeriod(selectedFiscalYear), [selectedFiscalYear]);

  const { data: summary, isLoading: summaryLoading } = useGetEmissionsSummaryQuery(
    {
      initiativeId: initiativeId ?? '',
      periodStart: periodDates.start,
      periodEnd: periodDates.end,
      includeChildren,
      scope2Method,
    },
    { skip: !initiativeId },
  );

  const { data: statsActivitiesData } = useGetActivitiesQuery(
    {
      initiativeId: initiativeId ?? '',
      scope: EmissionScope.Scope2,
      periodStart: periodDates.start,
      periodEnd: periodDates.end,
      includeChildren,
    },
    { skip: !initiativeId },
  );

  const { data: scope2DualSummary, isLoading: dualSummaryLoading } = useGetScope2DualSummaryQuery(
    { initiativeId: initiativeId ?? '', periodStart: periodDates.start, periodEnd: periodDates.end, includeChildren },
    { skip: !initiativeId },
  );

  const { data: activityTypeData, isLoading: activityTypeLoading } = useGetEmissionsByActivityTypeQuery(
    {
      initiativeId: initiativeId ?? '',
      periodStart: periodDates.start,
      periodEnd: periodDates.end,
      scopes: [EmissionScope.Scope2],
      includeChildren,
      scope2Method,
    },
    { skip: !initiativeId },
  );

  const statsActivities = useMemo(() => statsActivitiesData?.data || [], [statsActivitiesData?.data]);
  const scope2Total = summary?.byScope?.scope2 || 0;

  const locationBasedTotal = scope2DualSummary?.locationBased.total ?? scope2Total;
  const marketBasedTotal = scope2DualSummary?.marketBased.total ?? scope2Total;

  const totalEnergyConsumption = useMemo(() => {
    return statsActivities.reduce((sum, activity) => {
      if (activity.unit === ActivityUnit.KWh) return sum + activity.quantity;
      if (activity.unit === ActivityUnit.MWh) return sum + activity.quantity * 1000;
      return sum;
    }, 0);
  }, [statsActivities]);

  const marketBasedActivitiesCount = scope2DualSummary?.coveredByInstruments ?? 0;

  const energyBreakdown = useMemo(() => {
    if (!activityTypeData || activityTypeData.length === 0) return [];
    const total = activityTypeData.reduce((sum, item) => sum + item.total, 0);
    return activityTypeData
      .map((item) => {
        const color = ACTIVITY_TYPE_COLORS[item.activityType] || '#6c757d';
        const label = formatActivityType(item.activityType);
        return {
          source: label,
          icon: ACTIVITY_TYPE_FA_ICONS[item.activityType] ?? 'fa-leaf',
          percentage: total > 0 ? Math.round((item.total / total) * 100) : 0,
          color,
          emissions: Math.round(item.total / 1000),
        };
      })
      .sort((a, b) => b.emissions - a.emissions);
  }, [activityTypeData]);

  const handleGasClick = useCallback(
    (gas: 'co2' | 'ch4' | 'n2o') => {
      const url = generateUrlWithQuery(
        emissionsTrackerRoutes.EMISSIONS_ACTIVITIES,
        { rootAppPath, initiativeId },
        {
          scope: 'scope2',
          sort: gas,
          sortDir: 'desc',
          periodStart: periodDates.start,
          periodEnd: periodDates.end,
        },
      );
      history.push(url);
    },
    [initiativeId, periodDates, history],
  );

  return (
    <Dashboard>
      <EmissionsScopeNav />

      {/* Header */}
      <div className='tw:flex tw:justify-between tw:items-center'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1 tw:flex tw:items-center tw:gap-2'>
            <Zap className={cn('tw:size-5', SCOPE_TAILWIND.scope2.icon)} />
            Scope 2 Emissions
          </h4>
          <p className='tw:text-sm tw:text-muted-foreground'>
            Indirect emissions from purchased electricity, steam, heating, and cooling
          </p>
        </div>
        <div className='tw:flex tw:gap-2'>
          <IncludeChildrenToggle
            disabled={!hasChildren}
            includeChildren={includeChildren}
            onChange={setIncludeChildren}
          />
          <FiscalYearSelector
            selectedYear={selectedFiscalYear}
            onYearChange={setSelectedFiscalYear}
            years={availableYears}
          />
        </div>
      </div>

      <div className='tw:flex tw:items-end tw:justify-end tw:mb-4'>
        <TabNav
            variant='pills'
            size='sm'
            activeTab={scope2Method}
            onChange={setScope2Method}
            tabs={[
              { key: Scope2Method.LocationBased, label: 'Location', icon: <MapPin className='tw:size-3.5' /> },
              { key: Scope2Method.MarketBased, label: 'Market', icon: <ShoppingCart className='tw:size-3.5' /> },
            ]}
          />
      </div>
      
      {/* Dual Reporting Comparison Card */}
      <Scope2ComparisonCard
        locationBasedTotal={locationBasedTotal}
        marketBasedTotal={marketBasedTotal}
        totalEnergyConsumption={totalEnergyConsumption}
        marketBasedActivitiesCount={marketBasedActivitiesCount}
        activeMethod={scope2Method}
        isLoading={summaryLoading || dualSummaryLoading}
      />

      {/* Breakdowns + Supplementary — 3-column row */}
      <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:xl:grid-cols-3 tw:gap-6 tw:mb-6'>
        <GasBreakdownPanel
          byGas={summary?.byScopeGas?.scope2}
          isLoading={summaryLoading}
          onGasClick={handleGasClick}
          scope2Method={scope2Method}
        />

        <Card className='tw:h-full'>
          <CardHeader>
            <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
              <CardTitle>Energy Type Breakdown</CardTitle>
              <Button
                variant='link'
                size='sm'
                onClick={() => history.push(generateUrlWithQuery(
                  emissionsTrackerRoutes.EMISSIONS_INSIGHTS,
                  { rootAppPath, initiativeId },
                  { tab: 'sources' },
                ))}
              >
                View all
                <ArrowRight />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activityTypeLoading ? (
              <div className='tw:flex tw:items-center tw:justify-center' style={{ height: 220 }}>
                <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
              </div>
            ) : energyBreakdown.length === 0 ? (
              <div
                className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-muted-foreground'
                style={{ height: 220 }}
              >
                <Inbox className='tw:size-10 tw:mb-2 tw:opacity-50' />
                <p className='tw:text-sm'>No energy data</p>
              </div>
            ) : (
              <div className='tw:space-y-3'>
                <ChartContainer config={{}} style={{ height: 160 }} className='tw:w-full'>
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                    <Pie
                      data={energyBreakdown.map((d) => ({ name: d.source, emissions: d.emissions, fill: d.color }))}
                      dataKey='emissions'
                      nameKey='name'
                      innerRadius={45}
                      outerRadius={70}
                      strokeWidth={2}
                    >
                      {energyBreakdown.map((item) => (
                        <Cell key={item.source} fill={item.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ChartContainer>
                <div className='tw:space-y-1.5'>
                  {energyBreakdown.map((item) => (
                    <div key={item.source} className='tw:flex tw:items-center tw:justify-between'>
                      <div className='tw:flex tw:items-center tw:gap-1.5'>
                        <div
                          className='tw:rounded tw:flex tw:items-center tw:justify-center tw:shrink-0'
                          style={{ width: 20, height: 20, backgroundColor: `${item.color}22` }}
                        >
                          <i className={`fa ${item.icon}`} style={{ fontSize: '0.6rem', color: item.color }} />
                        </div>
                        <span className='tw:text-xs'>{item.source}</span>
                      </div>
                      <span className='tw:text-xs tw:font-medium'>{item.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Supplementary as third column — only rendered when there is data */}
        <Scope2SupplementaryPanel supplementary={scope2DualSummary?.supplementary} isLoading={dualSummaryLoading} />
      </div>

      {/* Asset Breakdown (Scope 2 filtered) */}
      <div className='tw:mb-6'>
        <AssetBreakdownPanel
          initiativeId={initiativeId}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          includeChildren={includeChildren}
          scope={EmissionScope.Scope2}
          variant='cards'
          onAssetSelect={(assetId) => {
            history.push(
              generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSET_DASHBOARD, {
                rootAppPath,
                initiativeId,
                assetId,
              }),
            );
          }}
        />
      </div>

      {/* Top Sources + Recent Activities summary row */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6 tw:mb-6'>
        <TopEmissionSourcesCard
          initiativeId={initiativeId ?? ''}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          scope={EmissionScope.Scope2}
          includeChildren={includeChildren}
        />
        <RecentActivitiesCard
          initiativeId={initiativeId ?? ''}
          scope={EmissionScope.Scope2}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          includeChildren={includeChildren}
          title={(view) => `${view === 'top' ? 'Top' : 'Latest'} 5 Scope 2 Activities`}
          viewAllUrl={(view) =>
            generateUrlWithQuery(
              emissionsTrackerRoutes.EMISSIONS_ACTIVITIES,
              { rootAppPath, initiativeId },
              view === 'top'
                ? { scope: 'scope2', sort: 'calculatedCO2e', sortDir: 'desc' }
                : { scope: 'scope2' },
            )
          }
          onViewCalculation={openDrawer}
          emptyAction={{
            label: 'Add Scope 2 activity',
            to: generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW, { rootAppPath, initiativeId }),
          }}
        />
      </div>

      <CalculationDetailsDrawer
        isOpen={selectedActivity !== null}
        toggle={closeDrawer}
        initiativeId={selectedActivity?.initiativeId ?? initiativeId}
        activityId={selectedActivity?._id ?? null}
        onRecalculate={handleRecalculate}
        isRecalculating={recalculatingActivityId !== null}
        activityData={
          selectedActivity
            ? {
                activityType: selectedActivity.activityType,
                bulkImportId: selectedActivity.bulkImportId,
                quantity: selectedActivity.quantity,
                unit: selectedActivity.unit,
                region: selectedActivity.region,
                periodStart: selectedActivity.periodStart,
                periodEnd: selectedActivity.periodEnd,
                scope: selectedActivity.scope,
                fuelType: selectedActivity.fuelType,
                vehicleType: selectedActivity.vehicleType,
                refrigerantType: selectedActivity.refrigerantType,
                processType: selectedActivity.processType,
                regionFallback: selectedActivity.regionFallback,
                yearFallback: selectedActivity.yearFallback,
                calculationNotices: selectedActivity.calculationNotices,
              }
            : undefined
        }
        reportingLevelLabel={selectedActivity ? getReportingLevelLabel(selectedActivity.initiativeId) : undefined}
      />
    </Dashboard>
  );
};
